<script setup>
import { Fancybox, Carousel,Panzoom } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox.css";
import "@fancyapps/ui/dist/panzoom.css";
import "@fancyapps/ui/dist/panzoom.controls.css";
import "@fancyapps/ui/dist/carousel.css";
import { ref,onMounted, computed, onBeforeUnmount, defineProps, getCurrentInstance } from 'vue';
import video_placeholder_url from "@/assets/images/video-placeholder.jpg"
import { useStore } from 'vuex'
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/forest/index.css';
import ClipboardJS from 'clipboard'

const player = ref(null);
const clipboard_filename = ref(null);

onMounted(()=>{
  initCarousel();

  // A record can be stills only — videojs() throws on a missing element, which
  // would take the whole viewer down with it.
  if (document.getElementById(videoPlayerId)) {
    player.value = videojs(videoPlayerId, {
      controls: true,
      autoplay: false,
      preload: 'auto',
      fluid: true
    });
  }

  clipboard_filename.value = new ClipboardJS('.clipboard_filename');
});

onBeforeUnmount(()=>{
  if (player.value) {
    player.value.dispose();
  }
  clipboard_filename.value.destroy();
});

const props = defineProps({
  size: {
    type: String,
    default: '800px'
  },
  medias:{
    type: [Array, Object, null],
    required: true
  },
  traffic_record:{
    type: [Object, null],
    required: true
  }
});

const store = useStore()

const mainCarouselRef = ref()
const thumbCarouselRef = ref()
const fancy_box = "gallery"+getCurrentInstance().uid;

// Per instance, the same way fancy_box already is. It was the literal string
// "videoPlayer", so two viewers on one page both resolved getElementById() to
// the FIRST one: the second player attached to the first one's element and the
// second video was dead. That is why callers could only ever show one at a time.
const videoPlayerId = "videoPlayer"+getCurrentInstance().uid;

const loading = computed(() => store.state.globals.loading);

const initCarousel = () =>{
   const mainCarousel = new Carousel(mainCarouselRef.value, {
    Dots: false,
      on: {
      createSlide: (carousel, slide) => {
        if(slide.$el.querySelector(".panzoom")){
          slide.Panzoom = new Panzoom(slide.$el.querySelector(".panzoom"), {
            panOnlyZoomed: true,
            resizeParent: true,
            maxScale: function () {
              return 50;
            },
          });
        }
      },
      deleteSlide: (carousel, slide) => {
        if (slide.Panzoom) {
          slide.Panzoom.destroy();
          slide.Panzoom = null;
        }
      },
    },
  });

  new Carousel(thumbCarouselRef.value, {
    Sync: {
      target: mainCarousel,
      friction: 0,
    },
    Dots: false,
    Navigation: false,
    center: true,
    infinite: false
  });

  Fancybox.bind('[data-fancybox="'+fancy_box+'"]', {
    Carousel: {
      on: {
        change: (that) => {
          mainCarousel.slideTo(mainCarousel.findPageForSlide(that.page), {
            friction: 0,
          });
        },
      },
    },
  });
};

const spaceKeyPressed = () =>{
  if(player.value.paused()){
    player.value.play();
  }else{
    player.value.pause();
  }
}

const rightKeyPressed = () =>{
  const currentTime = player.value.currentTime();
  const increment = 3; //seconds
  const finalTime = currentTime + increment;
  player.value.currentTime(finalTime)
}

const leftKeyPressed = () =>{
  const currentTime = player.value.currentTime();
  const increment = 3; //seconds
  const finalTime = currentTime - increment;
  player.value.currentTime(finalTime)
}

</script>

<template>
  <div class="col-12 mb-1">
    <b-overlay :show="loading">
      <div class="card">
        <div class="card-body overflow-auto">
          <div :style="{ width: props.size }" ref="mainCarouselRef" id="mainCarousel" class="carousel w-10/12 max-w-5xl mx-auto">
            <template v-for="media in props.medias" :key="media.media_url" >
              <div v-if="media.type == 'image'" class="text-center carousel__slide" >
                <p class="filename">
                    {{media.filename}}.{{media.extension}} 
                    <!-- <button type="button" class="clipboard_filename btn btn-outline-secondary btn-sm btn-icon" :data-clipboard-text="props.traffic_record.filename" data-toggle="tooltip" data-placement="top" title="Copy Filename"><i class="ri-file-copy-2-line fs-16"></i></button> -->
                </p>
                <div class="panzoom">
                  <img  class="img_preview panzoom__content" :data-lazy-src="media.media_url" />
                </div>
                <i class="ri-fullscreen-line full-screen-icon"  :data-src="media.media_url" :data-fancybox="fancy_box"></i>
              </div>
            </template>
            <template v-for="media in props.medias" :key="media.media_url" >
              <div v-if="media.type == 'video'"  class="carousel__slide text-center">
                <p class="filename">
                    {{media.filename}}.{{media.extension}} 
                    <!-- <button type="button" class="clipboard_filename btn btn-outline-secondary btn-sm btn-icon" :data-clipboard-text="props.traffic_record.filename" data-toggle="tooltip" data-placement="top" title="Copy Filename"><i class="ri-file-copy-2-line fs-16"></i></button> -->
                </p>
                <video 
                @keydown.space.prevent="spaceKeyPressed" 
                @keydown.right.prevent="rightKeyPressed"
                @keydown.left.prevent="leftKeyPressed"
                :id="videoPlayerId"
                class="video-js vjs-theme-forest">
                  <source :src="media.media_url">
                </video>
              </div>
            </template>
          </div>

          <div ref="thumbCarouselRef" id="thumbCarousel" class="carousel max-w-xl mx-auto">
            <template v-for="media in medias" :key="media.media_url" >
              <div v-if="media.type == 'image'" class="carousel__slide">
                <img  class="panzoom__content" :data-lazy-src="media.media_url" />
              </div>
            </template>
            <template v-for="media in props.medias" :key="media.media_url" >
              <div v-if="media.type == 'video'" class="carousel__slide">
                <img class="panzoom__content" :data-lazy-src="video_placeholder_url" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<style scoped>
#mainCarousel {
  margin: 0 auto 1rem auto;

  --carousel-button-color: #170724;
  --carousel-button-bg: #fff;
  --carousel-button-shadow: 0 2px 1px -1px rgb(0 0 0 / 20%),
    0 1px 1px 0 rgb(0 0 0 / 14%), 0 1px 3px 0 rgb(0 0 0 / 12%);

  --carousel-button-svg-width: 20px;
  --carousel-button-svg-height: 20px;
  --carousel-button-svg-stroke-width: 2.5;
}

#mainCarousel .carousel__slide {
  width: 100%;
  max-height:500px;
  overflow: hidden;
  padding: 0;
  position: relative;
  padding-top:10px;
}

.filename{
  margin-top:-10px;
}

#mainCarousel .carousel__button.is-prev {
  left: -1.5rem;
}

#mainCarousel .carousel__button.is-next {
  right: -1.5rem;
}

#mainCarousel .carousel__button:focus {
  outline: none;
  box-shadow: 0 0 0 4px #A78BFA;
}

#thumbCarousel .carousel__slide {
  opacity: 0.5;
  padding: 0;
  margin: 0.25rem;
  width: 96px;
  height: 64px;
}

#thumbCarousel .carousel__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.img_preview {
  width: 100%;
}

#thumbCarousel .carousel__slide.is-nav-selected {
  opacity: 1;
}

.panzoom{
  height:90%;
}

.filename{
  margin-bottom: 5px;
}

.full-screen-icon{
  font-size:35px;
  color:gray;
  position:absolute;
  top:25px;
  right:28px;
  z-index: 999999999999999999;
}

</style>

<style>
.fancybox__container {
  z-index: 100000 !important;
}

.vue3-player-video .rounded-xl{
  border-radius:0 !important;
}

</style>
